import React, { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { 
  CheckCircle2, 
  AlertTriangle, 
  Clock, 
  XCircle,
  Building2,
  RefreshCw,
  Search,
  ChevronDown,
  ChevronUp,
  FileText,
  Wrench
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// Status colors
const STATUS_STYLES = {
  "Compliant": "bg-green-100 text-green-700 border-green-200",
  "Due Soon": "bg-amber-100 text-amber-700 border-amber-200",
  "Overdue": "bg-red-100 text-red-700 border-red-200",
  "Failed": "bg-red-200 text-red-800 border-red-300",
  "No Due Date": "bg-slate-100 text-slate-600 border-slate-200",
};

const STATUS_ICONS = {
  "Compliant": <CheckCircle2 className="h-4 w-4 text-green-500" />,
  "Due Soon": <Clock className="h-4 w-4 text-amber-500" />,
  "Overdue": <AlertTriangle className="h-4 w-4 text-red-500" />,
  "Failed": <XCircle className="h-4 w-4 text-red-600" />,
  "No Due Date": <FileText className="h-4 w-4 text-slate-400" />,
};

export default function PPMCompliancePage() {
  const [parks, setParks] = useState([]);
  const [selectedParkId, setSelectedParkId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [expandedUnits, setExpandedUnits] = useState({});

  // Load parks on mount
  useEffect(() => {
    loadParks();
  }, []);

  const loadParks = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/api/parks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setParks(res.data || []);
    } catch (err) {
      console.error("Failed to load parks:", err);
    }
  };

  const loadCompliance = async (parkId) => {
    if (!parkId) return;
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/api/ppm/compliance/park/${parkId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data);
    } catch (err) {
      console.error("Failed to load compliance:", err);
      toast.error("Failed to load compliance data");
    } finally {
      setLoading(false);
    }
  };

  const handleParkChange = (parkId) => {
    setSelectedParkId(parkId);
    loadCompliance(parkId);
  };

  const toggleUnit = (unit) => {
    setExpandedUnits(prev => ({ ...prev, [unit]: !prev[unit] }));
  };

  // Filter rows
  const filteredRows = data?.rows?.filter(row => {
    if (statusFilter !== "all" && row.status !== statusFilter) return false;
    if (search) {
      const searchLower = search.toLowerCase();
      if (
        !row.unit_name?.toLowerCase().includes(searchLower) &&
        !row.template_name?.toLowerCase().includes(searchLower) &&
        !row.trade_type?.toLowerCase().includes(searchLower)
      ) return false;
    }
    return true;
  }) || [];

  // Group by unit
  const unitGroups = filteredRows.reduce((acc, row) => {
    const unit = row.unit_name || "Unknown Unit";
    if (!acc[unit]) acc[unit] = [];
    acc[unit].push(row);
    return acc;
  }, {});

  return (
    <div className="p-6 space-y-6 bg-slate-50 min-h-screen" data-testid="ppm-compliance-page">
      {/* Header */}
      <div className="rounded-2xl bg-gradient-to-r from-teal-700 to-teal-600 p-6 text-white">
        <h1 className="text-2xl font-bold" data-testid="page-title">PPM Compliance</h1>
        <p className="text-teal-100 text-sm mt-1">Park-level compliance view with unit and template breakdowns</p>
      </div>

      {/* Park Selector */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex-1 min-w-[200px]">
              <Select value={selectedParkId} onValueChange={handleParkChange}>
                <SelectTrigger data-testid="park-selector">
                  <Building2 className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Select a Park" />
                </SelectTrigger>
                <SelectContent>
                  {parks.map(park => (
                    <SelectItem key={park.id} value={park.id}>{park.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {data && (
              <>
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Search units, templates..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-10"
                    data-testid="search-input"
                  />
                </div>
                
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-40" data-testid="status-filter">
                    <SelectValue placeholder="All Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="Compliant">Compliant</SelectItem>
                    <SelectItem value="Due Soon">Due Soon</SelectItem>
                    <SelectItem value="Overdue">Overdue</SelectItem>
                    <SelectItem value="Failed">Failed</SelectItem>
                  </SelectContent>
                </Select>
                
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => loadCompliance(selectedParkId)}
                  data-testid="refresh-btn"
                >
                  <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                </Button>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* No Park Selected */}
      {!selectedParkId && (
        <Card>
          <CardContent className="p-12 text-center">
            <Building2 className="h-12 w-12 mx-auto text-slate-300 mb-4" />
            <p className="text-slate-500">Select a park to view PPM compliance</p>
          </CardContent>
        </Card>
      )}

      {/* Loading */}
      {loading && (
        <Card>
          <CardContent className="p-12 text-center">
            <RefreshCw className="h-8 w-8 mx-auto text-teal-600 animate-spin mb-4" />
            <p className="text-slate-500">Loading compliance data...</p>
          </CardContent>
        </Card>
      )}

      {/* Data Display */}
      {data && !loading && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Card className="border-l-4 border-l-teal-500" data-testid="total-card">
              <CardContent className="p-4">
                <div className="text-sm text-slate-500">Total Schedules</div>
                <div className="text-3xl font-bold text-teal-700">{data.summary?.total || 0}</div>
              </CardContent>
            </Card>
            
            <Card className="border-l-4 border-l-green-500" data-testid="compliant-card">
              <CardContent className="p-4">
                <div className="text-sm text-slate-500">Compliant</div>
                <div className="text-3xl font-bold text-green-600">{data.summary?.compliant || 0}</div>
                <div className="text-xs text-green-500">{data.summary?.compliance_pct || 0}%</div>
              </CardContent>
            </Card>
            
            <Card className="border-l-4 border-l-amber-500" data-testid="due-soon-card">
              <CardContent className="p-4">
                <div className="text-sm text-slate-500">Due Soon</div>
                <div className="text-3xl font-bold text-amber-600">{data.summary?.due_soon || 0}</div>
              </CardContent>
            </Card>
            
            <Card className="border-l-4 border-l-red-500" data-testid="overdue-card">
              <CardContent className="p-4">
                <div className="text-sm text-slate-500">Overdue</div>
                <div className="text-3xl font-bold text-red-600">{data.summary?.overdue || 0}</div>
              </CardContent>
            </Card>
            
            <Card className="border-l-4 border-l-red-700" data-testid="failed-card">
              <CardContent className="p-4">
                <div className="text-sm text-slate-500">Failed</div>
                <div className="text-3xl font-bold text-red-700">{data.summary?.failed || 0}</div>
              </CardContent>
            </Card>
          </div>

          {/* Template Summary */}
          {Object.keys(data.template_summary || {}).length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <Wrench className="h-4 w-4 text-teal-600" />
                  By Template Type
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {Object.entries(data.template_summary || {}).map(([template, stats]) => (
                    <div key={template} className="border rounded-lg p-3 bg-white">
                      <div className="text-sm font-medium truncate">{template}</div>
                      <div className="flex items-center gap-2 mt-1 text-xs">
                        <span className="text-slate-500">{stats.total} total</span>
                        {stats.overdue > 0 && (
                          <span className="text-red-500">{stats.overdue} overdue</span>
                        )}
                        {stats.failed > 0 && (
                          <span className="text-red-700">{stats.failed} failed</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Schedule Items by Unit */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Schedule Items ({filteredRows.length})</CardTitle>
            </CardHeader>
            <CardContent>
              {Object.keys(unitGroups).length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  <FileText className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>No schedules found</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {Object.entries(unitGroups).map(([unit, items]) => (
                    <div key={unit} className="border rounded-lg overflow-hidden">
                      {/* Unit Header */}
                      <button
                        onClick={() => toggleUnit(unit)}
                        className="w-full flex items-center justify-between p-4 bg-slate-50 hover:bg-slate-100 transition-colors"
                        data-testid={`unit-header-${unit}`}
                      >
                        <div className="flex items-center gap-3">
                          <Building2 className="h-4 w-4 text-slate-500" />
                          <span className="font-medium">{unit}</span>
                          <Badge variant="secondary">{items.length} items</Badge>
                          {items.some(i => i.status === "Overdue" || i.status === "Failed") && (
                            <Badge className="bg-red-100 text-red-700 border-red-200">
                              {items.filter(i => i.status === "Overdue" || i.status === "Failed").length} attention
                            </Badge>
                          )}
                        </div>
                        {expandedUnits[unit] ? (
                          <ChevronUp className="h-4 w-4 text-slate-400" />
                        ) : (
                          <ChevronDown className="h-4 w-4 text-slate-400" />
                        )}
                      </button>
                      
                      {/* Unit Items */}
                      {expandedUnits[unit] && (
                        <div className="divide-y">
                          {items.map((row) => (
                            <div 
                              key={row.schedule_id} 
                              className="p-4 bg-white hover:bg-slate-50 transition-colors"
                              data-testid={`schedule-row-${row.schedule_id}`}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    {STATUS_ICONS[row.status]}
                                    <span className="font-medium">{row.template_name}</span>
                                    <Badge className={`text-xs ${STATUS_STYLES[row.status]}`}>
                                      {row.status}
                                    </Badge>
                                  </div>
                                  <div className="flex items-center gap-4 text-xs text-slate-500 mt-1">
                                    <span>{row.trade_type}</span>
                                    <span>
                                      Last: {row.last_completed_date 
                                        ? new Date(row.last_completed_date).toLocaleDateString() 
                                        : "Never"}
                                    </span>
                                    <span>
                                      Due: {row.next_due_date 
                                        ? new Date(row.next_due_date).toLocaleDateString() 
                                        : "-"}
                                    </span>
                                  </div>
                                </div>
                                <div className="text-right text-sm">
                                  {row.active_job_reference ? (
                                    <span className="text-teal-600 font-medium">
                                      {row.active_job_reference}
                                    </span>
                                  ) : row.assigned_contractor_name ? (
                                    <span className="text-slate-500">
                                      {row.assigned_contractor_name}
                                    </span>
                                  ) : (
                                    <span className="text-slate-400">Unassigned</span>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
